/**
 * Pure composition layer for the unified school print packet — the
 * `/school/print` artifact that interleaves USDA production-record documents
 * and serve/pass-out sheets, day by day. Mirrors the shape of
 * `lib/cafeteria/print-plan.ts` (block ids shared with the renderer's
 * `data-print-block` tags; measured heights resolve to real page numbers via
 * `lib/print/page-plan`).
 *
 * The packet is day-interleaved — each day's record forms print immediately
 * before that day's serve sheets — because a kitchen works one day at a
 * time; per-document-type grouping would make the morning ritual a shuffle.
 * Like the serve packet, there are NO cross-day totals anywhere.
 */

import type { ServiceHistoryDayWire } from '@/api/cafeteria'
import type { AdminSchool } from '@/api/school-orders'
import { formatShortServiceDateLabel, parseIsoDateParam } from '@/lib/cafeteria/format'
import type { RosterGrouping, ServePrintDay } from '@/lib/cafeteria/print-plan'
import { serveMealtimeBlockId } from '@/lib/cafeteria/print-plan'
import { compareMealtimes, type Mealtime, MEALTIME_LABELS, parseMealtimeParam } from '@/lib/mealtime'
import type { BlockOrientation, PagePlanStructure } from '@/lib/print/page-plan'
import type { AlreadyOrdered } from '@/lib/school-orders/already-ordered'
import { buildMenuOrderColumns, type OrderColumn } from '@/lib/school-orders/columns'

// ---------------------------------------------------------------------------
// Job shape
// ---------------------------------------------------------------------------

export interface SchoolPrintRecordDoc {
  /** ProductionRecord id — the document fan-out + block id key. */
  id: string
  mealtime: Mealtime
  /**
   * Kitchen name from the records LIST row. The USDA document itself prints
   * the kitchen by address only (deliberate); on a multi-vendor day the
   * composing page renders this as a slim qualifier line above the document.
   */
  vendorName: string
  /** Unsigned records print as blank PREP COPY forms (write-in boxes). */
  blank: boolean
}

/** One item row on a day's Orders sheet — school + parent COMBINED counts. */
export interface SchoolPrintOrdersRow {
  menuItemId: string
  name: string
  /** One combined quantity per orders-doc column, index-aligned with `columns`. */
  cells: number[]
  total: number
}

export interface SchoolPrintOrdersMealtime {
  mealtime: Mealtime
  label: string
  rows: SchoolPrintOrdersRow[]
}

/**
 * A day's Orders sheet: every ordered item with school + parent quantities
 * COMBINED, bucketed by the campus's order columns. One sheet per day
 * (mealtime sub-tables) — the kitchen-facing "what to expect" summary the
 * cafeteria Orders lookup page prints.
 */
export interface SchoolPrintOrdersDoc {
  /**
   * Order-column headers (grade bands / groups / whole school). When the day
   * holds unattributed lines, the synthetic catch columns are appended as
   * the LAST entries — "Staff" (`STAFF_COLUMN_ID`) then "Other"
   * (`OTHER_COLUMN_ID`), each only when it holds quantity.
   */
  columns: Array<{ id: string; label: string; studentCount: number | null }>
  mealtimes: SchoolPrintOrdersMealtime[]
  /** Combined MEAL total for the day (system-milk lines excluded). Includes unattributed meals. */
  totalMeals: number
  /** False ⇒ ordering still open; the sheet carries the counts-can-change note. */
  ordersConfirmed: boolean
  /**
   * The day holds unattributed lines from campus-staff diners (teacher /
   * school admin / cafeteria worker self-orders) — the "Staff" column is
   * present and the sheet renders its footnote sentence.
   */
  hasStaffColumn: boolean
  /**
   * The day holds unattributed lines from anyone else (no grade group and
   * not staff) — the "Other" column is present and the sheet renders its
   * footnote sentence. Both flags false ⇒ the sheet is column-for-column
   * what the campus's bands alone produce.
   */
  hasOtherColumn: boolean
  /**
   * Set ONLY when the sheet was built with a column filter: the labels of
   * the buckets it is scoped to, in column order. The renderer prints an
   * "Includes only: …" line so a filtered sheet can never be mistaken for
   * the whole day. Absent on an unfiltered sheet.
   */
  scopeLabels?: string[]
}

export interface SchoolPrintJobDay {
  /** `YYYY-MM-DD`. */
  iso: string
  /** This day's record documents; [] on pass-out-only days. */
  records: SchoolPrintRecordDoc[]
  /** This day's serve sheets; null when the day has no printable serve data. */
  serveDay: ServePrintDay | null
  /** This day's combined Orders sheet; absent/null when the section is off or empty. */
  orders?: SchoolPrintOrdersDoc | null
  /**
   * Split Orders sheets — one doc per bucket (PROD-1335's "separate sheet
   * per homeroom"). Each entry is an ordinary single-bucket scoped doc, so
   * it prints exactly like a filtered sheet; the renderer gives each its
   * own block, and a block always starts a fresh page — that is what puts
   * one homeroom on one physical sheet. Set INSTEAD of `orders`, never
   * alongside it.
   */
  orderSheets?: SchoolPrintOrdersDoc[] | null
}

export interface SchoolPrintJob {
  schoolName: string
  rangeLabel: string
  printedAtLabel: string
  grouping: RosterGrouping
  /** Ascending by iso; days with nothing printable are dropped upstream. */
  days: SchoolPrintJobDay[]
}

// ---------------------------------------------------------------------------
// Orders sheet composition
// ---------------------------------------------------------------------------

/**
 * Column ids of the synthetic catch columns. Cannot collide with real
 * column ids (cuids, `WHOLE_SCHOOL`/`ALL_GRADES` sentinels, or tuple keys
 * containing `::`). "Staff" holds staff self-orders (the dominant
 * unattributed class); "Other" holds the rest.
 */
export const STAFF_COLUMN_ID = 'staff'
export const OTHER_COLUMN_ID = 'other'

/** The slice of `OrderColumn` (lib/school-orders/columns) the Orders sheet reads. */
export interface SchoolPrintOrdersColumnSource {
  columnId: string
  label: string
  studentCount: number | null
  /** Already-ordered lookup key (portion tier / tuple key / whole-school). */
  audienceId: string
}

/**
 * Build one day's Orders sheet from the already-ordered lookup.
 *
 * Order lines with NO column attribution (no portion tier, no group tuple —
 * read-time inference finds nothing for e.g. staff self-diner orders) get a
 * catch column instead of a band: the campus's columns are keyed by
 * attribution, so such a line structurally has no column, but a sheet that
 * drops it undercounts the kitchen's real demand (d298a81b: 13 printed vs
 * ~28 actual meals). The catch is labeled by WHO the meals are for —
 * "Staff" for staff self-orders (the server classifies the diner), "Other"
 * for the rest — because a kitchen sheet that says "Unassigned" hides what
 * it actually knows. Each column, its footnote sentence, and the extra cell
 * per row appear only when that kind holds quantity — a fully-attributed
 * day prints byte-for-byte as before. `totalMeals` is always attributed +
 * unattributed.
 *
 * Returns null when the day has nothing to print (or the campus resolved no
 * columns, in which case the caller has no grid to render).
 *
 * `columnFilter` (4f289cdc) scopes the sheet to a subset of the campus's
 * buckets — the grade band / homeroom columns the sheet already groups by.
 * Unset or empty ⇒ every column plus the catch columns, output identical to
 * an unfiltered build. Non-empty ⇒ only the named columns print, row and
 * meal totals recompute over that subset, and the catch columns are
 * EXCLUDED — unattributed lines belong to no grade/homeroom bucket, so a
 * scoped sheet deliberately leaves them out (`scopeLabels` makes the sheet
 * say so). A filter that matches none of the columns yields null rather
 * than silently widening back to the whole day.
 */
export function buildSchoolPrintOrdersDoc(params: {
  iso: string
  columns: ReadonlyArray<SchoolPrintOrdersColumnSource>
  lookup: Pick<
    AlreadyOrdered,
    'cellQty' | 'mealtimeGradeQty' | 'unattributedCellQty' | 'unattributedStaffCellQty' | 'unattributedMealtimeQty'
  >
  /** Item display names for the day: mealtime → menuItemId → name. */
  namesByMealtime: ReadonlyMap<Mealtime, ReadonlyMap<string, string>>
  ordersConfirmed: boolean
  /** Print only these buckets (`columnId`s). Unset/empty = no filter. */
  columnFilter?: ReadonlySet<string> | null
}): SchoolPrintOrdersDoc | null {
  const { iso, lookup, namesByMealtime, ordersConfirmed } = params
  const filter = params.columnFilter && params.columnFilter.size > 0 ? params.columnFilter : null
  const columns = filter ? params.columns.filter((col) => filter.has(col.columnId)) : params.columns
  if (columns.length === 0) {
    return null
  }
  const built = [...namesByMealtime.keys()].sort(compareMealtimes).flatMap((mealtime) => {
    const names = namesByMealtime.get(mealtime) ?? new Map<string, string>()
    const rows = [...names.entries()]
      .map(([menuItemId, name]) => {
        const attributed = columns.map((col) => lookup.cellQty(iso, mealtime, col.audienceId, menuItemId))
        const staff = filter ? 0 : lookup.unattributedStaffCellQty(iso, mealtime, menuItemId)
        const other = filter ? 0 : lookup.unattributedCellQty(iso, mealtime, menuItemId) - staff
        return { menuItemId, name, attributed, staff, other }
      })
      // The catch columns participate: an item whose ONLY quantities are
      // unattributed still earns its row.
      .filter((row) => row.attributed.reduce((sum, qty) => sum + qty, 0) + row.staff + row.other > 0)
      .sort((a, b) => a.name.localeCompare(b.name))
    return rows.length > 0 ? [{ mealtime, rows }] : []
  })
  if (built.length === 0) {
    return null
  }
  const hasStaffColumn = built.some((m) => m.rows.some((row) => row.staff > 0))
  const hasOtherColumn = built.some((m) => m.rows.some((row) => row.other > 0))
  return {
    columns: [
      ...columns.map((col) => ({ id: col.columnId, label: col.label, studentCount: col.studentCount })),
      ...(hasStaffColumn ? [{ id: STAFF_COLUMN_ID, label: 'Staff', studentCount: null }] : []),
      ...(hasOtherColumn ? [{ id: OTHER_COLUMN_ID, label: 'Other', studentCount: null }] : []),
    ],
    mealtimes: built.map(({ mealtime, rows }) => ({
      mealtime,
      label: MEALTIME_LABELS[mealtime],
      rows: rows.map(({ menuItemId, name, attributed, staff, other }) => {
        const cells = [...attributed, ...(hasStaffColumn ? [staff] : []), ...(hasOtherColumn ? [other] : [])]
        return { menuItemId, name, cells, total: cells.reduce((sum, qty) => sum + qty, 0) }
      }),
    })),
    totalMeals: built.reduce(
      (sum, m) =>
        sum +
        columns.reduce((s, col) => s + lookup.mealtimeGradeQty(iso, m.mealtime, col.audienceId), 0) +
        (filter ? 0 : lookup.unattributedMealtimeQty(iso, m.mealtime)),
      0,
    ),
    ordersConfirmed,
    hasStaffColumn,
    hasOtherColumn,
    ...(filter ? { scopeLabels: columns.map((col) => col.label) } : {}),
  }
}

/**
 * Split one day into one Orders sheet PER bucket (PROD-1335) — each an
 * ordinary single-column scoped build, so every sheet gets the same
 * row/total math and the same "Includes only" header a filtered sheet has.
 * The renderer gives each its own block, and a block starts a fresh page:
 * that is what puts one homeroom on one physical sheet.
 *
 * `columnFilter` still decides WHICH buckets print; the split only decides
 * whether they share a sheet. Buckets with nothing ordered that day build
 * null and drop out — an empty homeroom earns no blank page.
 *
 * Unattributed lines (staff / no group) belong to no bucket, so — exactly as
 * under the existing filter — they appear on NO split sheet. The split
 * sheets therefore PARTITION the attributed day: their totals sum to the
 * combined sheet's attributed total, never to its unattributed one.
 */
export function buildSplitSchoolPrintOrdersDocs(
  params: Omit<Parameters<typeof buildSchoolPrintOrdersDoc>[0], 'columnFilter'> & {
    columnFilter?: ReadonlySet<string> | null
  },
): SchoolPrintOrdersDoc[] {
  const filter = params.columnFilter && params.columnFilter.size > 0 ? params.columnFilter : null
  const inScope = filter ? params.columns.filter((col) => filter.has(col.columnId)) : params.columns
  return inScope.flatMap((col) => {
    const doc = buildSchoolPrintOrdersDoc({ ...params, columnFilter: new Set([col.columnId]) })
    return doc ? [doc] : []
  })
}

/**
 * The campus buckets an Orders sheet can print — the union of the locked
 * lunch-style and breakfast-style partitions on AGE_GROUP campuses (an
 * untagged menu set spans every partition band), deduped by column; every
 * other dimension keeps its campus vocabulary columns; NONE campuses get
 * the single whole-school column. The ONE derivation the print page and the
 * print dialog's filter chips both read, so the filter can only ever offer
 * exactly the buckets the sheet renders.
 */
export function buildSchoolPrintOrderColumns(school: AdminSchool): OrderColumn[] {
  const seen = new Map(
    [...buildMenuOrderColumns(school, 'LUNCH', [[]]), ...buildMenuOrderColumns(school, 'BREAKFAST', [[]])].map(
      (col) => [col.columnId, col],
    ),
  )
  return [...seen.values()]
}

// ---------------------------------------------------------------------------
// Block ids — shared with the renderer's data-print-block wrappers.
// ---------------------------------------------------------------------------

export const SCHOOL_PRINT_COVER_BLOCK_ID = 'school-cover'

export function schoolRecordBlockId(recordId: string): string {
  return `record|${recordId}`
}

export function schoolOrdersBlockId(iso: string, bucketId?: string): string {
  return bucketId ? `orders|${iso}|${bucketId}` : `orders|${iso}`
}

/** The four kinds of block the packet can hold, in print order per day. */
export type SchoolPrintBlockKind = 'cover' | 'orders' | 'record' | 'serve'

/**
 * Which page box a packet block prints on — the SINGLE source of truth,
 * read by `buildSchoolPrintStructure` (which feeds `resolvePlan`'s page
 * numbers) AND by `SchoolPrintRoot` (which emits `data-print-orientation`
 * for the measure host). Declaring it twice is how the cover manifest
 * starts lying: a block measured against one page box and paginated
 * against the other is silently off by a page, with no type error and no
 * failing test. Orientation is a function of block KIND, never of data, so
 * `schoolPlanSignature` needs no change.
 *
 * PROD-1279: the USDA production record is the one landscape block — its
 * eleven columns do not fit portrait.
 */
export function schoolBlockOrientation(kind: SchoolPrintBlockKind): BlockOrientation {
  return kind === 'record' ? 'landscape' : 'portrait'
}

/**
 * The day's printable Orders sheets, in print order — the split sheets when
 * the day was built per-bucket, else the single combined sheet. Each carries
 * the bucket id its block is keyed by (undefined for the combined sheet, so
 * unsplit block ids are byte-identical to before). Empty docs are dropped: a
 * bucket with no orders that day earns no paper.
 */
export function dayOrdersSheets(
  day: SchoolPrintJobDay,
): Array<{ doc: SchoolPrintOrdersDoc; bucketId: string | undefined }> {
  const split = (day.orderSheets ?? []).filter((doc) => doc.mealtimes.length > 0)
  if (split.length > 0) {
    // A split sheet is built scoped to exactly one bucket, so its first
    // column IS that bucket (the catch columns are excluded under a filter).
    return split.map((doc) => ({ doc, bucketId: doc.columns[0]?.id }))
  }
  return day.orders && day.orders.mealtimes.length > 0 ? [{ doc: day.orders, bucketId: undefined }] : []
}

// ---------------------------------------------------------------------------
// Structure + signature
// ---------------------------------------------------------------------------

/** Total printable documents (orders sheets + record forms + serve sections) in the job. */
export function schoolPrintDocumentCount(job: SchoolPrintJob): number {
  return job.days.reduce(
    (n, day) => n + dayOrdersSheets(day).length + day.records.length + (day.serveDay?.mealtimes.length ?? 0),
    0,
  )
}

/** Documents in the job that are NOT record forms: Orders sheets and serving sheets. */
function schoolNonRecordDocumentCount(job: SchoolPrintJob): number {
  return job.days.reduce((n, day) => n + dayOrdersSheets(day).length + (day.serveDay?.mealtimes.length ?? 0), 0)
}

/**
 * A cover manifest earns its page when the packet holds more than one
 * document AND at least one of them is not a record form. A records-only
 * packet prints the forms straight away (PROD-1279 stage-1 feedback A4: the
 * manifest read as a blank leading page on a batch of record forms); mixed
 * packets and Orders-only packets keep their manifest, whose page numbers
 * are how a multi-day stack is navigated.
 */
export function schoolCoverIncluded(job: SchoolPrintJob): boolean {
  return schoolPrintDocumentCount(job) > 1 && schoolNonRecordDocumentCount(job) > 0
}

/**
 * Which `@page` geometry the packet page emits. `landscape` = every block
 * in the job is a landscape record, so the page declares ONE plain
 * `@page { size: letter landscape }` (the mechanism `/production/:id/print`
 * already uses) and no named page: a single-orientation job has no reason
 * for a page-name switch beside its forced breaks (a suspected blank-page
 * cause in Chrome), and Safari, which ignores named pages, prints it
 * landscape too. `mixed` = the job holds a portrait block (cover, Orders,
 * serving sheet), so the records need the named `@page record` that
 * `SchoolPrintRoot` binds with `print-page-record`. An empty job is `mixed`
 * (nothing to flip).
 */
export type SchoolPacketPageMode = 'landscape' | 'mixed'

export function schoolPacketPageMode(job: SchoolPrintJob): SchoolPacketPageMode {
  const { blocks } = buildSchoolPrintStructure(job)
  return blocks.length > 0 && blocks.every((block) => block.orientation === 'landscape') ? 'landscape' : 'mixed'
}

/**
 * Records sort mealtime-chronological, then by vendor name so a two-vendor
 * mealtime prints deterministically (never by id — that's insertion order).
 */
export function sortedDayRecords(records: readonly SchoolPrintRecordDoc[]): SchoolPrintRecordDoc[] {
  return [...records].sort(
    (a, b) =>
      compareMealtimes(a.mealtime, b.mealtime) || a.vendorName.localeCompare(b.vendorName) || a.id.localeCompare(b.id),
  )
}

/**
 * Block list + TOC for the measured page plan: cover first (when included),
 * then per day ascending — the Orders sheet (the day's overview), then
 * record forms, then serve sections.
 */
export function buildSchoolPrintStructure(job: SchoolPrintJob): PagePlanStructure {
  const blocks: PagePlanStructure['blocks'] = []
  const toc: PagePlanStructure['toc'] = []
  if (schoolCoverIncluded(job)) {
    blocks.push({ id: SCHOOL_PRINT_COVER_BLOCK_ID, orientation: schoolBlockOrientation('cover') })
  }
  const multiDay = job.days.length > 1
  for (const day of job.days) {
    const dayPrefix = multiDay ? `${formatShortServiceDateLabel(day.iso)} — ` : ''
    for (const { doc, bucketId } of dayOrdersSheets(day)) {
      const id = schoolOrdersBlockId(day.iso, bucketId)
      // A split sheet names its bucket in the TOC — five identical "Orders
      // sheet" rows for one day would be unnavigable.
      const scope = doc.scopeLabels?.length === 1 ? ` — ${doc.scopeLabels[0]}` : ''
      blocks.push({ id, orientation: schoolBlockOrientation('orders') })
      toc.push({ label: `${dayPrefix}Orders sheet${scope}`, firstBlockId: id })
    }
    for (const record of sortedDayRecords(day.records)) {
      blocks.push({ id: schoolRecordBlockId(record.id), orientation: schoolBlockOrientation('record') })
      toc.push({
        label: `${dayPrefix}${MEALTIME_LABELS[record.mealtime]} record${record.blank ? ' (prep copy)' : ''}`,
        firstBlockId: schoolRecordBlockId(record.id),
      })
    }
    for (const mealtime of day.serveDay?.mealtimes ?? []) {
      blocks.push({
        id: serveMealtimeBlockId(day.iso, mealtime.mealtime),
        orientation: schoolBlockOrientation('serve'),
      })
      toc.push({
        label: `${dayPrefix}${mealtime.label} serving sheet`,
        firstBlockId: serveMealtimeBlockId(day.iso, mealtime.mealtime),
      })
    }
  }
  return { blocks, toc }
}

/**
 * Layout identity for the measurement cache — record ids + blank-ness (a
 * filled form and a prep copy have different heights), each serve day's row
 * geometry, the grouping, and the day set.
 */
export function schoolPlanSignature(job: SchoolPrintJob): string {
  const days = job.days.map((day) => {
    const records = sortedDayRecords(day.records)
      .map((r) => `${r.id}:${r.blank ? 'b' : 'f'}`)
      .join(',')
    const serve = (day.serveDay?.mealtimes ?? [])
      .map((m) => {
        const rosterRows = m.rosterGroups.reduce((n, g) => n + g.students.length, 0)
        const mealLines = m.rosterGroups.reduce((n, g) => n + g.students.reduce((s, r) => s + r.mealLines.length, 0), 0)
        const tallyRows = m.tallyGroups.reduce((n, g) => n + g.rows.length, 0)
        return `${m.mealtime}:rg${m.rosterGroups.length}:r${rosterRows}:ml${mealLines}:t${tallyRows}`
      })
      .join('~')
    // Orders-sheet geometry: row count per mealtime table + the confirmed
    // flag (the still-open note adds height) + column count. A scoped sheet
    // adds the "Includes only" header line, so the filter marks the
    // signature too (appended only when present — unfiltered strings are
    // unchanged).
    // Split sheets contribute one segment each (joined by '+'), so a
    // combined sheet and its split equivalent never share a signature.
    const orders = dayOrdersSheets(day)
      .map(
        ({ doc }) =>
          (doc.mealtimes.map((m) => `${m.mealtime}:r${m.rows.length}`).join('~') || 'empty') +
          `:k${doc.columns.length}:c${doc.ordersConfirmed ? 1 : 0}` +
          (doc.scopeLabels && doc.scopeLabels.length > 0 ? `:f${doc.scopeLabels.length}` : ''),
      )
      .join('+')
    return `${day.iso}:c${day.serveDay?.ordersConfirmed === false ? 0 : 1}[${records}][${serve}][${orders}]`
  })
  return `g:${job.grouping}|${days.join('|')}`
}

// ---------------------------------------------------------------------------
// Cover manifest
// ---------------------------------------------------------------------------

export interface SchoolPrintManifestRow {
  iso: string
  dayLabel: string
  /** e.g. "2 record forms · Lunch serving sheet" or "No service". */
  documentsLabel: string
  /** Serve meal total for the day, or null on record-only days. */
  meals: number | null
  /** False ⇒ ordering still open, counts can move. Null when unknown. */
  ordersConfirmed: boolean | null
  /** First page of the day's first block; null for no-document days. */
  firstPage: number | null
}

function documentsLabel(day: SchoolPrintJobDay): string {
  const parts: string[] = []
  const sheets = dayOrdersSheets(day)
  if (sheets.length > 0) {
    // Split days say how many — "Orders sheet" for five of them would
    // under-promise the paper the day actually produces.
    parts.push(sheets.length === 1 ? 'Orders sheet' : `${sheets.length} Orders sheets`)
  }
  if (day.records.length > 0) {
    parts.push(`${day.records.length} record ${day.records.length === 1 ? 'form' : 'forms'}`)
  }
  for (const mealtime of day.serveDay?.mealtimes ?? []) {
    parts.push(`${mealtime.label} serving sheet`)
  }
  return parts.length > 0 ? parts.join(' · ') : 'No service'
}

/**
 * One manifest row per day in the PRINT RANGE — including gap days with
 * nothing to print, so "why is Tuesday missing?" is answered on paper. Days
 * outside `job.days` render as "No service" with no page number.
 */
export function buildSchoolPrintManifest(
  job: SchoolPrintJob,
  rangeDays: readonly string[],
  startPages: ReadonlyMap<string, number>,
): SchoolPrintManifestRow[] {
  const byIso = new Map(job.days.map((day) => [day.iso, day] as const))
  return rangeDays.map((iso) => {
    const day = byIso.get(iso)
    if (!day) {
      return {
        iso,
        dayLabel: formatShortServiceDateLabel(iso),
        documentsLabel: 'No service',
        meals: null,
        ordersConfirmed: null,
        firstPage: null,
      }
    }
    const firstRecord = sortedDayRecords(day.records)[0]
    const sheets = dayOrdersSheets(day)
    // Blocks render orders → records → serve; the manifest points at the
    // first — on a split day, at the first bucket sheet.
    const firstBlockId = sheets[0]
      ? schoolOrdersBlockId(day.iso, sheets[0].bucketId)
      : firstRecord
        ? schoolRecordBlockId(firstRecord.id)
        : day.serveDay && day.serveDay.mealtimes[0]
          ? serveMealtimeBlockId(day.iso, day.serveDay.mealtimes[0].mealtime)
          : null
    return {
      iso,
      dayLabel: day.serveDay?.shortDateLabel ?? formatShortServiceDateLabel(iso),
      documentsLabel: documentsLabel(day),
      meals: day.serveDay
        ? day.serveDay.mealtimes.reduce((n, m) => n + m.totalUnits, 0)
        : // Split sheets partition the day, so their totals SUM to the
          // day's demand (scoped sheets exclude the catch columns alike).
          sheets.length > 0
          ? sheets.reduce((n, sheet) => n + sheet.doc.totalMeals, 0)
          : null,
      ordersConfirmed: day.serveDay?.ordersConfirmed ?? (sheets[0] ? sheets[0].doc.ordersConfirmed : null),
      firstPage: firstBlockId ? (startPages.get(firstBlockId) ?? null) : null,
    }
  })
}

// ---------------------------------------------------------------------------
// Dialog pass-out composition
// ---------------------------------------------------------------------------

/**
 * What the pass-out sheets in a selection will actually hold. `parent` ⇒
 * named student rosters only, `school` ⇒ anonymous forecast tallies only,
 * `mixed` ⇒ both. `unknown` means the selection has no mealtimes to read
 * (empty range, still loading) — callers should fall back to the generic
 * combined wording.
 */
export type PassoutComposition = 'parent' | 'school' | 'mixed' | 'unknown'

/**
 * Derived from the history mealtimes' batch-originator flags, over the
 * SELECTED days only — a campus that runs both order methods but has a
 * forecast-only week selected genuinely prints no rosters, so roster-only
 * affordances (A–Z grouping, the student-names privacy note) must not show.
 */
export function passoutComposition(
  historyDays: readonly ServiceHistoryDayWire[],
  selectedDays: readonly string[],
): PassoutComposition {
  const selected = new Set(selectedDays)
  let hasParent = false
  let hasSchool = false
  for (const day of historyDays) {
    if (!selected.has(day.serviceDate)) {
      continue
    }
    for (const mealtime of day.mealtimes) {
      hasParent = hasParent || mealtime.hasParentOrders
      hasSchool = hasSchool || mealtime.hasSchoolOrders
    }
  }
  if (hasParent && hasSchool) {
    return 'mixed'
  }
  if (hasParent) {
    return 'parent'
  }
  if (hasSchool) {
    return 'school'
  }
  return 'unknown'
}

// ---------------------------------------------------------------------------
// Dialog day options
// ---------------------------------------------------------------------------

export interface SchoolPrintDayOption {
  iso: string
  /** Something is printable this day (any mealtime or record). */
  hasService: boolean
  recordCount: number
  /** Records that print FILLED (SUBMITTED/AMENDED). */
  filledCount: number
  /**
   * The day has service but zero record rows — orders exist only as batches
   * (beyond the materializer's ~14-day lookahead). The dialog explains
   * "record forms appear once orders are placed" instead of silently
   * printing fewer documents than the pill promised.
   */
  notMaterialized: boolean
}

/** Unsigned-record predicate — the ONE blank/filled rule, shared everywhere. */
export function recordPrintsBlank(status: string): boolean {
  return status !== 'SUBMITTED' && status !== 'AMENDED'
}

export function buildSchoolPrintDayOptions(
  historyDays: readonly ServiceHistoryDayWire[],
  rangeDays: readonly string[],
): SchoolPrintDayOption[] {
  const byIso = new Map(historyDays.map((day) => [day.serviceDate, day] as const))
  return rangeDays.map((iso) => {
    const day = byIso.get(iso)
    const recordCount = day?.productionRecords.length ?? 0
    const hasService = Boolean(day && (day.mealtimes.length > 0 || recordCount > 0))
    return {
      iso,
      hasService,
      recordCount,
      filledCount: day?.productionRecords.filter((r) => !recordPrintsBlank(r.status)).length ?? 0,
      notMaterialized: hasService && recordCount === 0,
    }
  })
}

// ---------------------------------------------------------------------------
// Legacy URL mappers — the old print routes 301 into /school/print with
// faithful selections, so bookmarked links keep producing the same paper.
// ---------------------------------------------------------------------------

/** `/production/print?locationId&from&to` → records-only school print URL. */
export function mapLegacyBatchParams(params: URLSearchParams): string | null {
  const locationId = params.get('locationId')
  const from = parseIsoDateParam(params.get('from'))
  const to = parseIsoDateParam(params.get('to'))
  if (!locationId || !from || !to || to < from) {
    return null
  }
  const out = new URLSearchParams({ locationId, from, to, sections: 'records' })
  return `/school/print?${out.toString()}`
}

/**
 * `/cafeteria/serve/print?locationId&start&mealtime&scope[&dates]` →
 * pass-out-only school print URL. `scope=mealtime` narrows via `mealtimes=`;
 * `dates` becomes an explicit day selection inside its own [min,max] range.
 */
export function mapLegacyServePrintParams(params: URLSearchParams, fallbackIso: string): string | null {
  const locationId = params.get('locationId')
  if (!locationId) {
    return null
  }
  const start = parseIsoDateParam(params.get('start')) ?? parseIsoDateParam(fallbackIso)
  const dates = (params.get('dates') ?? '')
    .split(',')
    .map((raw) => parseIsoDateParam(raw.trim()))
    .filter((iso): iso is string => iso != null)
    .sort()
  const days = dates.length > 0 ? [...new Set(dates)] : start ? [start] : []
  if (days.length === 0) {
    return null
  }
  const out = new URLSearchParams({
    locationId,
    from: days[0]!,
    to: days[days.length - 1]!,
    sections: 'passout',
  })
  if (days.length > 1) {
    out.set('days', days.join(','))
  }
  const mealtime = parseMealtimeParam(params.get('mealtime'))
  if (params.get('scope') !== 'day' && mealtime) {
    out.set('mealtimes', mealtime)
  }
  return `/school/print?${out.toString()}`
}

// ---------------------------------------------------------------------------
// Mealtimes row (PROD-1279 part two)
// ---------------------------------------------------------------------------

export interface SchoolPrintMealtimeOption {
  mealtime: Mealtime
  label: string
  /** Record forms this mealtime contributes across the selected days. */
  records: number
  /** ...of which are signed and print filled; the rest print blank. */
  signed: number
  /** Pass-out sheets this mealtime contributes across the selected days. */
  passout: number
}

/**
 * The Mealtimes row of the print dialog: every mealtime with a record or a
 * pass-out sheet on the SELECTED days, in time-of-day order, with the counts
 * the pills and the Documents section read. Empty until history loads, or
 * when nothing selected has service.
 */
export function buildSchoolPrintMealtimeOptions(
  historyDays: readonly ServiceHistoryDayWire[],
  selectedDays: readonly string[],
): SchoolPrintMealtimeOption[] {
  const selected = new Set(selectedDays)
  const byMealtime = new Map<Mealtime, SchoolPrintMealtimeOption>()
  const optionFor = (mealtime: Mealtime): SchoolPrintMealtimeOption => {
    let option = byMealtime.get(mealtime)
    if (!option) {
      option = { mealtime, label: MEALTIME_LABELS[mealtime], records: 0, signed: 0, passout: 0 }
      byMealtime.set(mealtime, option)
    }
    return option
  }
  for (const day of historyDays) {
    if (!selected.has(day.serviceDate)) {
      continue
    }
    for (const record of day.productionRecords) {
      const option = optionFor(record.mealtime)
      option.records += 1
      if (!recordPrintsBlank(record.status)) {
        option.signed += 1
      }
    }
    for (const mealtime of day.mealtimes) {
      optionFor(mealtime.mealtime).passout += 1
    }
  }
  return [...byMealtime.values()].sort((a, b) => compareMealtimes(a.mealtime, b.mealtime))
}
